import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { MessageSquare } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SmsComposeDialog({ open, onClose, lead }) {
  const { token } = useAuth();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) setMessage('');
  }, [open]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error('Bitte Nachricht eingeben');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/sms/trigger`, {
        lead_id: lead.id,
        phone_number: lead.phone,
        lead_name: lead.name,
        message: message.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`SMS an Handy gesendet: ${lead.name}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  };

  if (!lead) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="sms-compose-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <MessageSquare className="w-5 h-5 text-blue-600" />
            SMS senden
          </DialogTitle>
          <DialogDescription>
            Nachricht an {lead.name} ({lead.phone})
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSend} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Empfänger</Label>
            <Input
              readOnly
              value={`${lead.name} · ${lead.phone}`}
              className="rounded-sm font-mono text-sm bg-slate-50"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sms-message">Nachricht *</Label>
            <Textarea
              id="sms-message"
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Hallo Herr/Frau ..., ..."
              className="rounded-sm min-h-[120px]"
              data-testid="sms-message-input"
            />
            <p className="text-xs text-slate-400">{message.length} Zeichen</p>
          </div>
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-sm" data-testid="sms-cancel-button">
              Abbrechen
            </Button>
            <Button
              type="submit"
              disabled={sending || !message.trim()}
              className="rounded-sm bg-blue-600 text-white hover:bg-blue-700"
              data-testid="sms-send-button"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              {sending ? 'Senden...' : 'An Handy senden'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
